/**
 * Theme Context
 * Provides dark mode toggle and theme persistence
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { useDirection } from '../hooks/useDirection';

interface ThemeContextType {
    mode: PaletteMode;
    toggleTheme: () => void;
    setThemeMode: (mode: PaletteMode) => void;
}

const ThemeContext = createContext<ThemeContextType>({
    mode: 'light',
    toggleTheme: () => { },
    setThemeMode: () => { },
});

export const useThemeMode = () => useContext(ThemeContext);

interface ThemeProviderProps {
    children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const { isRTL } = useDirection();

    // Initialize theme from localStorage or system preference
    const [mode, setMode] = useState<PaletteMode>(() => {
        const savedMode = localStorage.getItem('themeMode');
        if (savedMode === 'light' || savedMode === 'dark') {
            return savedMode;
        }
        // Check system preference
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    });

    // Save theme preference to localStorage
    useEffect(() => {
        localStorage.setItem('themeMode', mode);
    }, [mode]);

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            const savedMode = localStorage.getItem('themeMode');
            if (!savedMode) {
                setMode(e.matches ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleTheme = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const setThemeMode = (newMode: PaletteMode) => {
        setMode(newMode);
    };

    // Create theme based on mode — inspired by mobile app's soft teal/blue gradient style
    const theme = useMemo(
        () =>
            createTheme({
                direction: isRTL ? 'rtl' : 'ltr',
                palette: {
                    mode,
                    primary: {
                        main: mode === 'light' ? '#2196F3' : '#64B5F6',
                        light: mode === 'light' ? '#64B5F6' : '#90CAF9',
                        dark: mode === 'light' ? '#1565C0' : '#42A5F5',
                        contrastText: '#ffffff',
                    },
                    secondary: {
                        main: mode === 'light' ? '#00897B' : '#4DB6AC',
                        light: mode === 'light' ? '#4DB6AC' : '#80CBC4',
                        dark: mode === 'light' ? '#00695C' : '#26A69A',
                    },
                    success: {
                        main: '#4CAF50',
                        light: '#81C784',
                        dark: '#388E3C',
                    },
                    warning: {
                        main: '#F9A825',
                        light: '#FFD54F',
                        dark: '#F57F17',
                    },
                    error: {
                        main: '#EF5350',
                        light: '#EF9A9A',
                        dark: '#C62828',
                    },
                    info: {
                        main: '#29B6F6',
                        light: '#81D4FA',
                        dark: '#0288D1',
                    },
                    background: {
                        default: mode === 'light' ? '#F0F4F8' : '#0F1924',
                        paper: mode === 'light' ? '#FFFFFF' : '#1A2332',
                    },
                    text: {
                        primary: mode === 'light' ? '#1A2138' : 'rgba(255, 255, 255, 0.92)',
                        secondary: mode === 'light' ? '#5A6478' : 'rgba(255, 255, 255, 0.6)',
                    },
                    divider: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                    action: {
                        active: mode === 'light' ? 'rgba(0, 0, 0, 0.54)' : 'rgba(255, 255, 255, 0.56)',
                        hover: mode === 'light' ? 'rgba(33, 150, 243, 0.04)' : 'rgba(100, 181, 246, 0.08)',
                        selected: mode === 'light' ? 'rgba(33, 150, 243, 0.08)' : 'rgba(100, 181, 246, 0.16)',
                    },
                },
                typography: {
                    fontFamily: '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
                    h1: {
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                    },
                    h2: {
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                    },
                    h3: {
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                    },
                    h4: {
                        fontWeight: 600,
                    },
                    h5: {
                        fontWeight: 600,
                    },
                    h6: {
                        fontWeight: 600,
                    },
                    subtitle1: {
                        fontWeight: 500,
                        fontSize: '1.05rem',
                    },
                    body1: {
                        lineHeight: 1.7,
                    },
                    body2: {
                        lineHeight: 1.6,
                    },
                    button: {
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                    },
                },
                shape: {
                    borderRadius: 12,
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                scrollbarWidth: 'thin',
                                '&::-webkit-scrollbar': {
                                    width: '6px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)',
                                    borderRadius: '3px',
                                },
                            },
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                textTransform: 'none',
                                borderRadius: 10,
                                padding: '8px 20px',
                                fontSize: '0.9rem',
                                transition: 'all 0.2s ease-in-out',
                            },
                            contained: {
                                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.25)',
                                '&:hover': {
                                    boxShadow: '0 4px 16px rgba(33, 150, 243, 0.35)',
                                    transform: 'translateY(-1px)',
                                },
                            },
                            outlined: {
                                borderWidth: '1.5px',
                                '&:hover': {
                                    borderWidth: '1.5px',
                                    backgroundColor: mode === 'light' ? 'rgba(33, 150, 243, 0.04)' : 'rgba(100, 181, 246, 0.08)',
                                },
                            },
                        },
                    },
                    MuiCard: {
                        styleOverrides: {
                            root: {
                                borderRadius: 16,
                                border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.06)',
                                boxShadow: mode === 'light'
                                    ? '0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04)'
                                    : '0 1px 3px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    boxShadow: mode === 'light'
                                        ? '0 4px 12px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.06)'
                                        : '0 4px 12px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.3)',
                                    transform: 'translateY(-2px)',
                                },
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                                borderRadius: 12,
                            },
                            elevation1: {
                                boxShadow: mode === 'light'
                                    ? '0 1px 3px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)'
                                    : '0 1px 3px rgba(0, 0, 0, 0.4)',
                            },
                        },
                    },
                    MuiAppBar: {
                        styleOverrides: {
                            root: {
                                background: mode === 'light'
                                    ? 'linear-gradient(135deg, #5DADE2 0%, #3498DB 50%, #2E86C1 100%)'
                                    : 'linear-gradient(135deg, #1A2332 0%, #1E3A5F 100%)',
                                boxShadow: mode === 'light'
                                    ? '0 2px 12px rgba(52, 152, 219, 0.3)'
                                    : '0 2px 12px rgba(0, 0, 0, 0.4)',
                                backdropFilter: 'blur(8px)',
                            },
                        },
                    },
                    MuiFab: {
                        styleOverrides: {
                            root: {
                                boxShadow: '0 4px 16px rgba(33, 150, 243, 0.35)',
                                '&:hover': {
                                    boxShadow: '0 6px 24px rgba(33, 150, 243, 0.45)',
                                    transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            },
                        },
                    },
                    MuiChip: {
                        styleOverrides: {
                            root: {
                                borderRadius: 8,
                                fontWeight: 500,
                                fontSize: '0.8rem',
                            },
                            colorPrimary: {
                                background: mode === 'light'
                                    ? 'linear-gradient(135deg, #64B5F6, #42A5F5)'
                                    : undefined,
                            },
                            colorSuccess: {
                                background: mode === 'light'
                                    ? 'linear-gradient(135deg, #81C784, #66BB6A)'
                                    : undefined,
                            },
                        },
                    },
                    MuiTab: {
                        styleOverrides: {
                            root: {
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                minHeight: 48,
                                '&.Mui-selected': {
                                    fontWeight: 600,
                                },
                            },
                        },
                    },
                    MuiTabs: {
                        styleOverrides: {
                            indicator: {
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                            },
                        },
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 10,
                                    transition: 'all 0.2s ease',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: mode === 'light' ? '#64B5F6' : '#42A5F5',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderWidth: 2,
                                    },
                                },
                            },
                        },
                    },
                    MuiDialog: {
                        styleOverrides: {
                            paper: {
                                borderRadius: 16,
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                            },
                        },
                    },
                    MuiMenu: {
                        styleOverrides: {
                            paper: {
                                borderRadius: 12,
                                boxShadow: mode === 'light'
                                    ? '0 4px 20px rgba(0, 0, 0, 0.1)'
                                    : '0 4px 24px rgba(0, 0, 0, 0.6), 0 1px 6px rgba(0, 0, 0, 0.4)',
                                border: mode === 'light'
                                    ? '1px solid rgba(0, 0, 0, 0.08)'
                                    : '1px solid rgba(255, 255, 255, 0.14)',
                                backgroundColor: mode === 'light' ? '#ffffff' : '#243044',
                            },
                        },
                    },
                    MuiMenuItem: {
                        styleOverrides: {
                            root: {
                                borderRadius: 8,
                                margin: '2px 6px',
                                padding: '8px 12px',
                                color: mode === 'light' ? 'inherit' : 'rgba(255, 255, 255, 0.92)',
                                '&:hover': {
                                    backgroundColor: mode === 'light'
                                        ? 'rgba(33, 150, 243, 0.08)'
                                        : 'rgba(100, 181, 246, 0.16)',
                                },
                                '& .MuiListItemIcon-root': {
                                    color: mode === 'light' ? 'inherit' : 'rgba(255, 255, 255, 0.7)',
                                },
                            },
                        },
                    },
                    MuiAvatar: {
                        styleOverrides: {
                            root: {
                                border: `2px solid ${mode === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)'}`,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            },
                        },
                    },
                    MuiAlert: {
                        styleOverrides: {
                            root: {
                                borderRadius: 12,
                            },
                        },
                    },
                    MuiLinearProgress: {
                        styleOverrides: {
                            root: {
                                borderRadius: 4,
                                height: 6,
                            },
                        },
                    },
                    MuiStepper: {
                        styleOverrides: {
                            root: {
                                padding: '24px 0',
                            },
                        },
                    },
                    MuiCardMedia: {
                        styleOverrides: {
                            root: {
                                borderRadius: '16px 16px 0 0',
                            },
                        },
                    },
                },
            }),
        [mode, isRTL]
    );

    const contextValue = useMemo(
        () => ({
            mode,
            toggleTheme,
            setThemeMode,
        }),
        [mode]
    );

    return (
        <ThemeContext.Provider value={contextValue}>
            <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
        </ThemeContext.Provider>
    );
};
